



















import { computed, defineComponent, getCurrentInstance, onBeforeUnmount, ref } from "@vue/composition-api";
import { CreditCardAssignment } from "@scrile/api-provider/dist/api/CreditCardAssignmentsProvider";
import { KycVerificationStatus } from "@scrile/api-provider/dist/api/KycPropertiesProvider";
import { PurchaseSubjectTypeEnum } from "@scrile/api-provider/dist/api/PurchaseTypeProvider";
import { RecurringBillingScheduleSubjectTypeEnum } from "@scrile/api-provider/dist/api/RecurringBillingScheduleProvider";
import { Subject } from "@scrile/api-provider/dist/api/SubjectProvider";
import { Transaction } from "@scrile/api-provider/dist/api/TransactionProvider";
import { isScrileWebView } from "@scrile/tools/dist/lib/browserUtils";
import useAuthorisedOnly from "@/hooks/useAuthorisedOnly";
import useAuthUser from "@/hooks/useAuthUser";
import { usePostMessage } from "@/hooks/usePostMessage";
import useSystemSettings from "@/hooks/useSystemSettings";
import providers from "@/lib/api";
import createDeferred from "@/lib/deferredPromise";
import emitter from "@/lib/emitter";
import { getMainCurrencyCode } from "@/lib/moneyHelpers";
import CallsLogger from "@/modules/callsLogger";
import getIntegration from "@/payments/integrations";
import { OnStartPurchaseData, PurchaseResponse, SubscriptionResponse } from "@/payments/types";
import config from "@/config";
import { EVENTS } from "@/const";
import ModalView from "@/components/ModalView";

export default defineComponent({
  name: "PurchaseManager",
  components: { ModalView },
  setup() {
    const vm = getCurrentInstance();
    const { authUserStrict, identityStatus, getIdentityStatus } = useAuthUser();
    const show = ref(false);
    const loading = ref(false);
    const processingRequest = ref(false);
    const { creditCardAllowed, currentPaymentSystem } = useSystemSettings();
    const pricePackageId = ref<string>("");
    const price = ref("");
    const purchaseSubject = ref<Subject<PurchaseSubjectTypeEnum> | null>(null);
    const subscriptionSubject = ref<Subject<RecurringBillingScheduleSubjectTypeEnum> | null>(null);
    let timer: ReturnType<typeof setTimeout> | null = null;

    const integrationModule = computed(() => getIntegration(currentPaymentSystem.value));
    const forceShowUI = computed(() => integrationModule.value?.forceShowUI ?? false);

    const closeOnError = ref(false);

    const { sendMessage, getAsyncMessage } = usePostMessage<any>(window, config.webviewTargetOrigin);
    const closeModal = (payload: PurchaseResponse | SubscriptionResponse) => {
      emitter.emit(!subscriptionSubject.value ? EVENTS.PAYMENT_PURCHASED : EVENTS.PAYMENT_SUBSCRIBED, payload);
      pricePackageId.value = "";
      price.value = "";
      show.value = false;
      subscriptionSubject.value = null;
      closeOnError.value = false;
      timer = null;
    };

    const onClose = () => {
      if (processingRequest.value) return;
      closeModal({
        status: false,
        subjectPurchased: false,
      });
    };

    const checkIdentity = async (changeCb?: () => void) => {
      if (!config.featureKycVerification) return;
      await getIdentityStatus();
      if (identityStatus.value === KycVerificationStatus.UNDER_VERIFICATION) {
        timer && clearTimeout(timer);
        changeCb && changeCb();
        vm?.$router.push({ name: "identityVerification" });
      }
    };

    const checkChangeStatus = (promise: Promise<any>, resolve: (v: any) => void) => {
      const check = async () => {
        await checkIdentity(() => resolve({ status: false, subjectPurchased: false }));
        if (identityStatus.value !== KycVerificationStatus.UNDER_VERIFICATION) timer = setTimeout(() => check(), 5000);
      };
      check();
      return promise;
    };

    const startPurchase = async (data?: any) => {
      try {
        processingRequest.value = true;
        if (!pricePackageId.value || !currentPaymentSystem.value) {
          throw new Error("Cant find payment information");
        }
        const { promise, resolve } = createDeferred();

        let payload: PurchaseResponse | SubscriptionResponse = await Promise.race([
          subscriptionSubject.value
            ? integrationModule.value.subscribe(
                {
                  currency: getMainCurrencyCode(),
                  paymentSystemId: currentPaymentSystem.value.id,
                  subject: subscriptionSubject.value,
                },
                data
              )
            : integrationModule.value.purchase(
                {
                  currency: getMainCurrencyCode(),
                  paymentSystemId: currentPaymentSystem.value.id,
                  pricePackageId: pricePackageId.value,
                },
                data
              ),
          ...(config.featureKycVerification ? [checkChangeStatus(promise, resolve)] : []),
        ]);

        resolve();
        closeModal(payload);
      } finally {
        processingRequest.value = false;
      }
    };

    const onSubmit = (data?: any) =>
      startPurchase(data).catch(e => {
        // eslint-disable-next-line no-console
        console.error(e);
        if (closeOnError.value) {
          closeModal({
            status: false,
            subjectPurchased: false,
            error: e,
          });
        }
      });

    const startPurchaseIOSWebView = async ({ pricePackageId, price, purchaseSubject }: OnStartPurchaseData) => {
      try {
        if (!pricePackageId) throw new Error("Cant find payment information");
        await CallsLogger.collectLogs("Start ios WebView purchase", { pricePackageId, streaming: false });
        let transaction: Transaction | undefined,
          receipt = "",
          status = false;

        await sendMessage({ data: { price, pricePackageId } });
        const messageData = await getAsyncMessage();
        await CallsLogger.collectLogs("Received payment data", { data: messageData, streaming: false });

        try {
          const data = JSON.parse(messageData);
          status = data.status;
          receipt = data.receipt;
        } catch (e) {
          await CallsLogger.collectLogs("Payment ios WebView error", { data: messageData, error: e, streaming: false });
        }

        if (status) {
          transaction = await providers.PurchaseProvider.purchaseCreateAppleStore({
            data: { receipt, subject: purchaseSubject },
          });
          await checkIdentity();
        }

        closeModal({
          status: !!transaction,
          subjectPurchased: !!purchaseSubject && !!transaction,
          transaction,
        });
      } finally {
        await CallsLogger.collectLogs("Finished ios WebView purchase", { pricePackageId, streaming: false });
        processingRequest.value = false;
      }
    };

    const onStartPurchase = useAuthorisedOnly(
      async ({
        pricePackageId: newPricePackageId,
        price: newPrice = "",
        subscriptionSubject: newSubscriptionSubject,
        purchaseSubject: newPurchaseSubject,
      }: OnStartPurchaseData) => {
        price.value = newPrice;
        pricePackageId.value = newPricePackageId;
        subscriptionSubject.value = newSubscriptionSubject ?? null;
        purchaseSubject.value = newPurchaseSubject ?? null;
        try {
          loading.value = true;
          if (isScrileWebView() && !subscriptionSubject.value) {
            try {
              await startPurchaseIOSWebView({
                price: price.value,
                pricePackageId: pricePackageId.value,
                ...(purchaseSubject.value ? { purchaseSubject: purchaseSubject.value } : {}),
              });
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error(e);
              closeModal({
                status: false,
                subjectPurchased: false,
                error: e,
              });
            }
            return;
          } else if (!forceShowUI.value) {
            let cardsAssignmentData: CreditCardAssignment[] = [];
            if (creditCardAllowed.value) {
              cardsAssignmentData = await providers.CreditCardAssignmentsProvider.findAll({
                userId: authUserStrict.value.id,
              });
            }
            if (cardsAssignmentData.length > 0) {
              try {
                await startPurchase();
              } catch (e) {
                // eslint-disable-next-line no-console
                console.error(e);
                closeModal({
                  status: false,
                  subjectPurchased: false,
                  error: e,
                });
              }
              return;
            }
          }
          show.value = true;
        } finally {
          loading.value = false;
        }
      }
    );
    emitter.on<any>(EVENTS.PAYMENT_START_PURCHASE, onStartPurchase);

    onBeforeUnmount(() => {
      emitter.off<any>(EVENTS.PAYMENT_START_PURCHASE, onStartPurchase);
    });

    return {
      loading,
      show,
      processingRequest,
      integrationModule,
      price,
      currentPaymentSystem,
      closeOnError,
      pricePackageId,
      purchaseSubject,
      subscriptionSubject,
      onSubmit,
      onClose,
      identityStatus,
    };
  },
});
